'use client';

import { Box } from '@mui/material';
import { createContext, useMemo, type PropsWithChildren } from 'react';

import { Loader } from '@/components/layout/loading';
import type { nonProjectDalFactory } from '@/data/dal/dalFactory';
import { projectDalFactory } from '@/data/dal/dalFactory';

import { useEnvironment } from './useEnvironment';
import { useNonProjectDal } from './useNonProjectDal';
import { useUser } from './useUser';

export const DalContext = createContext<
  | (ReturnType<typeof projectDalFactory> &
      ReturnType<typeof nonProjectDalFactory>)
  | null
>(null);

export const DalProvider = ({ children }: PropsWithChildren) => {
  const { currentProjectId } = useUser();

  if (!currentProjectId) {
    return <LoaderComponent />;
  }

  return <ProviderWrapper>{children}</ProviderWrapper>;
};

const ProviderWrapper = ({ children }: PropsWithChildren) => {
  const { nonProjectDal, httpClient, configuration } = useNonProjectDal();
  const { currentProjectId } = useUser();
  const { isTest, isDev } = useEnvironment();

  const dal = useMemo(() => {
    const projectDal = projectDalFactory(
      httpClient,
      configuration,
      currentProjectId
    );

    const dal = { ...projectDal, ...nonProjectDal };

    if (isTest && typeof window !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, react-hooks/immutability
      (window as any).populateTestData = async () => {
        const urls = [
          `/projects/${currentProjectId}/populate?version=1`,
          `/projects/${currentProjectId}/metering/populate?version=1`,
          `/projects/${currentProjectId}/inventory/populate-backup-jobs?version=1`,
          `/projects/${currentProjectId}/visibility/populate?version=1`,
          `/projects/${currentProjectId}/inventory/populate?version=1`,
          `/projects/${currentProjectId}/search/populate?version=1`,
        ];

        for (const url of urls) {
          await httpClient.put(url, '{}');
        }
      };
    }

    if (isDev && typeof window !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, react-hooks/immutability
      (window as any).dal = dal;
    }

    return dal;
  }, [
    configuration,
    currentProjectId,
    isTest,
    httpClient,
    isDev,
    nonProjectDal,
  ]);

  return <DalContext.Provider value={dal}>{children}</DalContext.Provider>;
};

const LoaderComponent = () => (
  <Box
    className='h-full'
    sx={{
      background: 'var(--mui-palette-background-pageGradient)',
    }}
  >
    <Loader />
  </Box>
);
